/**
 * Fishing — US 20.01/20.03, spec in 110 §2. Deterministic replay
 * validation: the server generates the fish schedule from a seed, the
 * client renders THAT schedule (it never sees the species table — weights
 * and values stay server-side), records catches, and the server replays
 * physics to validate every claim. One invalid claim voids the session.
 *
 * Geometry is normalised: depth 0 (surface) … 1 (floor); horizontal
 * position 0 (left) … 1 (right); the hook line sits at x = 0.5. All
 * tuning constants live here so client and server can never disagree.
 */
import { rng } from './procgen.ts';

export interface SpeciesEntry {
  itemId: string;
  /** Normalised depth band this species swims in. Deeper = rarer (20.01). */
  depthMin: number;
  depthMax: number;
  /** Horizontal crossing speed in playfield-widths per second. */
  speed: number;
  /** Relative spawn weight within the table. */
  spawnWeight: number;
}

export interface FishSpawn {
  index: number;
  spawnTimeMs: number;
  depth: number;
  /** widths/second; sign = direction (+ left→right, − right→left). */
  speed: number;
  itemId: string;
}

export interface CatchClaim {
  fishIndex: number;
  catchTimeMs: number;
  hookDepth: number;
}

// 108-style tuning constants — shared verbatim client↔server.
export const HOOK_SPEED = 0.55; // depth units per second
export const HOOK_RESET_MS = 1000; // surface return after a catch (20.01)
export const CATCH_DEPTH_TOLERANCE = 0.06;
export const CATCH_X_TOLERANCE = 0.06;
export const HOOK_X = 0.5;
export const SESSION_MS = 60_000;
export const SPAWN_EVERY_MS = 1500;

/** The full deterministic schedule — same seed, same fish, always. */
export function generateSchedule(
  seed: number,
  durationMs: number,
  table: readonly SpeciesEntry[],
): FishSpawn[] {
  const random = rng(seed);
  const totalWeight = table.reduce((n, s) => n + s.spawnWeight, 0);
  const spawns: FishSpawn[] = [];
  let index = 0;
  for (let t = 1000; t < durationMs; t += SPAWN_EVERY_MS) {
    let pick = random() * totalWeight;
    let species = table[table.length - 1]!;
    for (const s of table) {
      pick -= s.spawnWeight;
      if (pick <= 0) {
        species = s;
        break;
      }
    }
    const depth = species.depthMin + random() * (species.depthMax - species.depthMin);
    const dir = random() < 0.5 ? 1 : -1;
    spawns.push({
      index: index++,
      spawnTimeMs: t,
      depth,
      speed: species.speed * dir,
      itemId: species.itemId,
    });
  }
  return spawns;
}

/** Where a fish is at time t, or null if off the playfield. */
export function fishX(spawn: FishSpawn, atMs: number): number | null {
  const elapsed = (atMs - spawn.spawnTimeMs) / 1000;
  if (elapsed < 0) return null;
  const x = spawn.speed > 0 ? elapsed * spawn.speed : 1 + elapsed * spawn.speed;
  return x >= 0 && x <= 1 ? x : null;
}

export interface ReplayResult {
  ok: boolean;
  reason?: string;
  /** Item ids caught, in catch order — the basket (20.01). */
  caught: string[];
}

/**
 * Server-side replay (20.03): every claim must be physically possible —
 * the fish existed there and then, the hook was on it, and the hook could
 * actually have travelled between consecutive catches (speed + reset).
 * ONE invalid claim voids the whole session.
 */
export function replayCatches(
  schedule: readonly FishSpawn[],
  claims: readonly CatchClaim[],
  durationMs: number,
): ReplayResult {
  const sorted = [...claims].sort((a, b) => a.catchTimeMs - b.catchTimeMs);
  const caught: string[] = [];
  const taken = new Set<number>();
  let lastTimeMs = 0;
  let lastDepth = 0; // hook starts at the surface

  for (const claim of sorted) {
    const fish = schedule[claim.fishIndex];
    if (!fish || taken.has(claim.fishIndex)) {
      return {
        ok: false,
        reason: `claim on unknown or re-caught fish #${claim.fishIndex}`,
        caught: [],
      };
    }
    if (claim.catchTimeMs < 0 || claim.catchTimeMs > durationMs) {
      return { ok: false, reason: `catch outside the session window`, caught: [] };
    }
    const x = fishX(fish, claim.catchTimeMs);
    if (x === null || Math.abs(x - HOOK_X) > CATCH_X_TOLERANCE) {
      return {
        ok: false,
        reason: `fish #${claim.fishIndex} was not at the hook line at ${claim.catchTimeMs}ms`,
        caught: [],
      };
    }
    if (Math.abs(claim.hookDepth - fish.depth) > CATCH_DEPTH_TOLERANCE) {
      return {
        ok: false,
        reason: `hook depth ${claim.hookDepth.toFixed(2)} missed fish #${claim.fishIndex} at ${fish.depth.toFixed(2)}`,
        caught: [],
      };
    }
    // Travel feasibility: after the previous catch the hook resets to the
    // surface (HOOK_RESET_MS), then descends at HOOK_SPEED max.
    const available =
      (claim.catchTimeMs - lastTimeMs - (caught.length > 0 ? HOOK_RESET_MS : 0)) / 1000;
    const needed = caught.length > 0 ? claim.hookDepth : Math.abs(claim.hookDepth - lastDepth);
    if (available < 0 || needed > available * HOOK_SPEED + 1e-9) {
      return {
        ok: false,
        reason: `hook could not reach depth ${claim.hookDepth.toFixed(2)} by ${claim.catchTimeMs}ms`,
        caught: [],
      };
    }
    taken.add(claim.fishIndex);
    caught.push(fish.itemId);
    lastTimeMs = claim.catchTimeMs;
    lastDepth = claim.hookDepth;
  }
  return { ok: true, caught };
}
